/* eslint-disable @typescript-eslint/no-explicit-any */

function extractPageNumber(pageReference: string): number | null {
  if (!pageReference || typeof pageReference !== 'string') return null

  const MAX_PAGE_NUMBER = 999
  const trimmedRef = pageReference.trim()
  const pagePatterns = [
    /(?:^|[^\w])p\.\s*(\d+)(?:[/-]\d+)*(?=\D|$)/i,
    /(?:^|[^\w])page\s*(\d+)(?:[/-]\d+)*(?=\D|$)/i,
    /(?:^|[^\w])pg\s*(\d+)(?:[/-]\d+)*(?=\D|$)/i,
    /^(\d+)$/,
    /(?:see|refer|reference|section|clause)\s+.*?(\d+)/i,
    /(?:page\s+number|page\s+no\.?)\s*(\d+)/i,
  ]

  for (const pattern of pagePatterns) {
    const match = trimmedRef.match(pattern)
    if (match && match[1]) {
      const pageNumber = parseInt(match[1], 10)
      if (pageNumber > 0 && pageNumber <= MAX_PAGE_NUMBER) return pageNumber
    }
  }

  const hasPageContext = /(?:p\.|page|pg|refer|see|section|clause|paragraph)/i.test(trimmedRef)
  if (hasPageContext) {
    const numberMatch = trimmedRef.match(/\d+/)
    if (numberMatch) {
      const pageNumber = parseInt(numberMatch[0], 10)
      if (pageNumber > 0 && pageNumber <= MAX_PAGE_NUMBER) return pageNumber
    }
  }

  return null
}

function calculateStringSimilarity(str1: string, str2: string): number {
  const normalized1 = str1.toLowerCase().trim()
  const normalized2 = str2.toLowerCase().trim()

  if (normalized1 === normalized2) return 1
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) return 0.8

  const words1 = normalized1.split(/\s+/)
  const words2 = normalized2.split(/\s+/)
  const commonWords = words1.filter((word) =>
    words2.some((word2) => word2.includes(word) || word.includes(word2)),
  )

  if (commonWords.length > 0) return commonWords.length / Math.max(words1.length, words2.length)
  return 0
}

function getInsurerNames(selectedPolicyContents?: any[]): string[] {
  return selectedPolicyContents
    ? selectedPolicyContents.map((policy) => policy.policy.insurer.name)
    : []
}

function createInsurerPatterns(
  selectedPolicyContents?: any[],
): Array<{ pattern: RegExp; name: string }> {
  const insurerNames = getInsurerNames(selectedPolicyContents)

  return insurerNames.map((insurerName) => {
    const cleanName = insurerName
      .replace(/\s*\([^)]*\)/g, '')
      .replace(/\s*PDS\s*/gi, '')
      .replace(/\s*Limited\s*/gi, '')
      .replace(/\s*Ltd\s*/gi, '')
      .replace(/\s*Insurance\s*/gi, '')
      .replace(/\s*Group\s*/gi, '')
      .trim()

    const escapedOriginal = insurerName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const escapedClean = cleanName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const pattern = new RegExp(`(${escapedOriginal}|${escapedClean})`, 'i')

    return {
      pattern,
      name: insurerName,
    }
  })
}

function findPdfPathByPolicyCode(
  policyCode: string,
  allPolicies?: any[],
  selectedPolicyContents?: any[],
): string | null {
  if (selectedPolicyContents && selectedPolicyContents.length > 0) {
    for (const policyContent of selectedPolicyContents) {
      if (policyContent.policy?.code === policyCode && policyContent.policy?.wording_path) {
        return policyContent.policy.wording_path
      }
    }
  }

  if (allPolicies && allPolicies.length > 0) {
    const policy = allPolicies.find((p: any) => p.code === policyCode)
    if (policy?.wording_path) return policy.wording_path
  }

  console.warn(`Wording path not found for policy code: ${policyCode}`)
  return null
}

function findTmdUrlByPolicyCode(
  policyCode: string,
  allPolicies?: any[],
  selectedPolicyContents?: any[],
): string | null {
  if (selectedPolicyContents && selectedPolicyContents.length > 0) {
    for (const policyContent of selectedPolicyContents) {
      if (policyContent.policy?.code === policyCode && policyContent.policy?.tmd_url) {
        return policyContent.policy.tmd_url
      }
    }
  }

  if (allPolicies && allPolicies.length > 0) {
    const policy = allPolicies.find((p: any) => p.code === policyCode)
    if (policy?.tmd_url) return policy.tmd_url
    if (policy?.tmdUrl) return policy.tmdUrl
  }

  return null
}

function extractInsurerFromContext(
  fullResponse: string,
  pageReference?: string,
  referenceLineNumber?: number,
  selectedPolicyContents?: any[],
): { insurer: string; lineNumber: number } {
  const insurerPatterns = createInsurerPatterns(selectedPolicyContents)

  if (referenceLineNumber) {
    const lines = fullResponse.split('\n')
    let nearestInsurer = 'default'
    let nearestLineNumber = referenceLineNumber

    for (let i = referenceLineNumber - 1; i >= 0; i -= 1) {
      const line = lines[i]
      const headerMatch = line.match(/\*\*([^*]+):?\*\*/)
      if (headerMatch) {
        const headingText = headerMatch[1].trim()

        for (const insurer of insurerPatterns) {
          if (insurer.pattern.test(headingText)) {
            nearestInsurer = insurer.name
            nearestLineNumber = i + 1
            return { insurer: nearestInsurer, lineNumber: nearestLineNumber }
          }
        }

        if (nearestInsurer === 'default') {
          nearestInsurer = headingText
          nearestLineNumber = i + 1
        }
      }
    }

    return { insurer: nearestInsurer, lineNumber: nearestLineNumber }
  }

  const lines = fullResponse.split('\n')
  let currentPolicy = 'default'
  let currentLineNumber = 0

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]
    currentLineNumber = i + 1

    const headerMatch = line.match(/\*\*([^*]+):?\*\*/)
    if (headerMatch) {
      const headingText = headerMatch[1].trim()

      for (const insurer of insurerPatterns) {
        if (insurer.pattern.test(headingText)) {
          currentPolicy = insurer.name
          break
        }
      }

      if (currentPolicy === 'default') currentPolicy = headingText
      continue
    }

    if (pageReference && line.includes(pageReference)) {
      return { insurer: currentPolicy, lineNumber: currentLineNumber }
    }
  }

  const responseLower = fullResponse.toLowerCase()

  for (const insurer of insurerPatterns) {
    if (insurer.pattern.test(responseLower)) {
      return { insurer: insurer.name, lineNumber: 0 }
    }
  }

  return { insurer: 'default', lineNumber: 0 }
}

export {
  extractPageNumber,
  calculateStringSimilarity,
  getInsurerNames,
  createInsurerPatterns,
  findPdfPathByPolicyCode,
  findTmdUrlByPolicyCode,
  extractInsurerFromContext,
}
